import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { ArrowRepeat, CreditCardFill, Lightbulb, SendFill } from 'react-bootstrap-icons';

import CheckoutClientForm from './CheckoutClientForm';
import Confirmation from './Confirmation';
import {
  APPLICATIONS,
  formatCurrency,
  getAllowedPlansForApp,
  getApplicationBySku,
  getPlanBySku,
  PLANS,
} from '../data/serviceCatalog';

const QUERY_KEYS = {
  app: ['appSku', 'app', 'applicationSku', 'application', 'app_sku'],
  plan: ['planSku', 'plan', 'planId', 'plan_sku'],
};

const getFirstQueryValue = (params, keys) => {
  for (const key of keys) {
    const value = params.get(key);
    if (value) {
      return value;
    }
  }
  return null;
};

const removeSpaces = (value) => String(value || '').replace(/\s+/g, '');

const ProductCard = ({ title, subtitle, description, sku, priceLabel, onChange, warning }) => (
  <Card className="h-100 checkout-selection-card">
    <Card.Body className="d-flex flex-column h-100">
      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h6 className="mb-3">{title}</h6>

          {description ? <div className="text-muted small">{description}</div> : null}
        </div>
        <Button variant="outline-secondary" size="sm" onClick={onChange} className="d-inline-flex align-items-center gap-1">
          <ArrowRepeat size={14} />
          Cambiar
        </Button>
      </div>
      <div className="checkout-price-pill mt-auto align-self-start">{priceLabel}</div>
    </Card.Body>
  </Card>
);

const CheckoutFlow = () => {
  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const initialApp = useMemo(() => {
    const appSku = getFirstQueryValue(queryParams, QUERY_KEYS.app);
    return getApplicationBySku(appSku) || APPLICATIONS.find((app) => app.sku === 'PW001') || APPLICATIONS[0];
  }, [queryParams]);

  const initialPlan = useMemo(() => {
    const planSku = getFirstQueryValue(queryParams, QUERY_KEYS.plan);
    const selectedPlan = getPlanBySku(planSku);
    const allowedPlans = getAllowedPlansForApp(initialApp);

    if (selectedPlan && allowedPlans.some((plan) => plan.id === selectedPlan.id)) {
      return selectedPlan;
    }

    return allowedPlans[0] || PLANS[0];
  }, [initialApp, queryParams]);

  const [application, setApplication] = useState(initialApp);
  const [plan, setPlan] = useState(initialPlan);
  const [includeSignatureService, setIncludeSignatureService] = useState(null);
  const [clientData, setClientData] = useState({});
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAppModal, setShowAppModal] = useState(false);
  const [showSignatureInfoModal, setShowSignatureInfoModal] = useState(false);
  const [error, setError] = useState('');
  const [submissionMode, setSubmissionMode] = useState(null);
  const [isClientFormValid, setIsClientFormValid] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const clientSectionRef = useRef(null);
  const previousSignatureSelectedRef = useRef(false);

  const availablePlans = useMemo(() => getAllowedPlansForApp(application), [application]);

  const signatureSelected = typeof includeSignatureService === 'boolean';
  const signatureBasePrice = includeSignatureService === true ? 100 : 0;
  const subtotal = (application.price || 0) + (plan.price || 0) + signatureBasePrice;
  const itbms = subtotal * 0.07;
  const total = subtotal + itbms;
  const submitting = Boolean(submissionMode);
  const isPayDisabled = submitting || !signatureSelected || !isClientFormValid;
  const isQuoteDisabled = submitting || !signatureSelected || !isClientFormValid;

  useEffect(() => {
    const wasSelected = previousSignatureSelectedRef.current;

    if (!wasSelected && signatureSelected && clientSectionRef.current) {
      clientSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      clientSectionRef.current.focus({ preventScroll: true });
    }

    previousSignatureSelectedRef.current = signatureSelected;
  }, [signatureSelected]);

  const handleAppChange = (nextApp) => {
    setApplication(nextApp);

    const allowedPlansForApp = getAllowedPlansForApp(nextApp);
    if (!allowedPlansForApp.some((allowedPlan) => allowedPlan.id === plan.id)) {
      setPlan(allowedPlansForApp[0] || PLANS[0]);
    }

    setShowAppModal(false);
  };

  const handlePlanChange = (nextPlan) => {
    setPlan(nextPlan);
    setShowPlanModal(false);
  };

  const extractPaymentUrl = async (response) => {
    const payload = await response.json();
    return payload?.response?.link || null;
  };

  const buildRequestPayload = () => ({
    fecha_solicitud: new Date().toISOString(),
    cliente: {
      ruc: removeSpaces(clientData.ruc),
      dv: clientData.dv,
      nombre_razon_social: clientData.name,
      tipo_contribuyente: clientData.type,
      correo: removeSpaces(clientData.email),
      telefono: removeSpaces(clientData.phone) || null,
      nombre_contacto: clientData.contactPerson || null,
    },
    servicio_contratado: {
      aplicacion: {
        nombre: application.name,
        sku: application.sku,
      },
      plan_suscripcion: {
        sku: plan.id,
        folios_anuales: plan.folios,
        nombre_amigable: plan.friendlyName,
      },
    },
    incluir_gestion_firma_electronica: includeSignatureService,
  });

  const buildConfirmationData = () => ({
    client: clientData,
    service: {
      application: application.name,
      plan,
    },
    signature: {
      includeSignatureService,
    },
  });

  const handleCheckoutSubmit = async () => {
    setSubmissionMode('pay');
    setError('');
    const payload = buildRequestPayload();

    try {
      const response = await fetch('https://hub.efacturapty.com/estimate/pay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Solicitud fallida (${response.status}): ${errorText}`);
      }

      const paymentUrl = await extractPaymentUrl(response);

      if (!paymentUrl) {
        throw new Error('No se recibio un link de pago valido en la respuesta.');
      }

      window.location.assign(paymentUrl);
    } catch (submitError) {
      console.error('Error al iniciar checkout:', submitError);
      setError('No se pudo iniciar el checkout. Intenta nuevamente.');
      setSubmissionMode(null);
    }
  };

  const handleSendQuote = async () => {
    setSubmissionMode('quote');
    setError('');

    const payload = buildRequestPayload();

    try {
      const response = await fetch('https://hub.efacturapty.com/estimate/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Solicitud fallida (${response.status}): ${errorText}`);
      }

      setConfirmationData(buildConfirmationData());
      setShowConfirmation(true);
      setSubmissionMode(null);
    } catch (submitError) {
      console.error('Error al enviar cotizacion:', submitError);
      setError('No se pudo enviar la cotizacion. Intenta nuevamente.');
      setSubmissionMode(null);
    }
  };

  const handleNewQuote = () => {
    setIncludeSignatureService(null);
    setClientData({});
    setIsClientFormValid(false);
    setError('');
    setSubmissionMode(null);
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  if (showConfirmation && confirmationData) {
    return <Confirmation data={confirmationData} onNewQuote={handleNewQuote} />;
  }

  return (
    <div className="p-4">
      <p className="text-muted mb-4">
        Selecciona la aplicación y el plan de facturación.
      </p>

      <Row className="g-3 mb-4">
        <Col md={6}>
          <ProductCard
            title={application.name}
            subtitle={null}
            description={application.description}
            sku={application.sku}
            priceLabel={formatCurrency(application.price)}
            warning={
              ['QuickBooks', 'Zoho Books'].includes(application.name)
                ? 'Requiere plan de 1,200 folios o superior.'
                : null
            }
            onChange={() => setShowAppModal(true)}
          />
        </Col>
        <Col md={6}>
          <ProductCard
            title={plan.friendlyName}
            subtitle={`SKU: ${plan.id}`}
            description={plan.standardName}
            sku={plan.id}
            priceLabel={formatCurrency(plan.price)}
            onChange={() => setShowPlanModal(true)}
          />
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <h5 className="mb-0">Firma electrónica</h5>
            <Button
              variant="link"
              className="p-0 text-decoration-none d-inline-flex align-items-center gap-1"
              onClick={() => setShowSignatureInfoModal(true)}
            >
              <Lightbulb size={16} />
              ¿Qué es la firma electrónica?
            </Button>
          </div>
          <p className="text-muted mb-3">
            ¿Deseas incluir nuestro servicio de gestión de firma electrónica?
          </p>
          <p className="text-muted mb-3">
            Se incluye el servicio de gestión: Solicitud, pago del trámite, seguimiento e instalación de la firma electrónica en la cuenta. El tiempo de tramitación es de dos días.
          </p>
          <div className="d-flex flex-wrap align-items-center gap-2 mt-4 mb-4">
            <span className={`checkout-price-pill ${includeSignatureService === false ? 'checkout-price-pill-muted' : ''}`}>
              {formatCurrency(100)}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant={includeSignatureService === true ? 'primary' : 'outline-primary'}
              onClick={() => setIncludeSignatureService(true)}
              disabled={submitting}
            >
              Sí, incluir gestión
            </Button>
            <Button
              variant={includeSignatureService === false ? 'secondary' : 'outline-secondary'}
              onClick={() => setIncludeSignatureService(false)}
              disabled={submitting}
            >
              No, la tramitaré por mi cuenta
            </Button>
          </div>
        </Card.Body>
      </Card>

      {signatureSelected ? (
        <div ref={clientSectionRef} tabIndex={-1}>
          <CheckoutClientForm
            data={clientData}
            updateFormData={setClientData}
            submitting={submitting}
            onValidityChange={setIsClientFormValid}
          />

          <Card className="mt-4 checkout-total-card">
            <Card.Body>
              <h5 className="mb-3">Resumen final</h5>
              <div className="checkout-total-row">
                <span>Subtotal</span>
                <strong>{new Intl.NumberFormat('es-PA', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(subtotal)}</strong>
              </div>
              <div className="checkout-total-row">
                <span>ITBMS (7%)</span>
                <strong>{new Intl.NumberFormat('es-PA', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(itbms)}</strong>
              </div>
              <div className="checkout-total-row checkout-total-row-grand">
                <span>Total</span>
                <strong>{new Intl.NumberFormat('es-PA', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(total)}</strong>
              </div>

              <div className="d-flex justify-content-end flex-wrap gap-2 mt-3">
                <Button
                  onClick={handleSendQuote}
                  disabled={isQuoteDisabled}
                  variant="outline-primary"
                  className="d-inline-flex align-items-center gap-2"
                >
                  Enviar cotización
                  <SendFill size={18} />
                </Button>
                <Button
                  onClick={handleCheckoutSubmit}
                  disabled={isPayDisabled}
                  className="d-inline-flex align-items-center gap-2"
                >
                  Pagar y habilitar plan
                  <CreditCardFill size={18} />
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      ) : (
        <div className="checkout-callout checkout-callout-info mb-0">
          Selecciona una opcion de firma para continuar con los datos del cliente.
        </div>
      )}

      {submitting ? (
        <div className="d-flex align-items-center gap-2 mt-3 text-muted">
          <Spinner animation="border" size="sm" />
          {submissionMode === 'quote' ? 'Enviando cotización...' : 'Redirigiendo a la pasarela de pago...'}
        </div>
      ) : null}

      {error ? <div className="checkout-callout checkout-callout-error mt-3 mb-0">{error}</div> : null}

      <Modal show={showAppModal} onHide={() => setShowAppModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar aplicación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ListGroup>
            {APPLICATIONS.map((appOption) => (
              <ListGroup.Item
                action
                key={appOption.sku}
                active={appOption.sku === application.sku}
                onClick={() => handleAppChange(appOption)}
                className="d-flex justify-content-between align-items-start"
              >
                <div>
                  <div className="fw-semibold">{appOption.name}</div>
                  <div className="small text-muted">SKU: {appOption.sku}</div>
                  {appOption.description ? <div className="small text-muted">{appOption.description}</div> : null}
                </div>
                <div className="small">{formatCurrency(appOption.price)}</div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
      </Modal>

      <Modal show={showPlanModal} onHide={() => setShowPlanModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ListGroup>
            {availablePlans.map((planOption) => (
              <ListGroup.Item
                action
                key={planOption.id}
                active={planOption.id === plan.id}
                onClick={() => handlePlanChange(planOption)}
                className="d-flex justify-content-between align-items-start"
              >
                <div>
                  <div className="fw-semibold">{planOption.friendlyName}</div>
                  <div className="small text-muted">{planOption.standardName}</div>
                  <div className="small text-muted">SKU: {planOption.id}</div>
                </div>
                <div className="small">{formatCurrency(planOption.price)}</div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
      </Modal>

      <Modal show={showSignatureInfoModal} onHide={() => setShowSignatureInfoModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>¿Qué es la firma electrónica?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            La firma electrónica es un archivo de tipo certificado digital, contiene datos del contribuyente y se utiliza para firmar cada factura. De esta manera las facturas no pueden ser alteradas ni falsificadas.
          </p>
          <p className="mb-0">
            La DGI estableció que es obligatorio para la facturación con PAC, por lo que debes contar con una firma electrónica
            vigente para poder emitir facturas electrónicas.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSignatureInfoModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CheckoutFlow;
